/*
 * MyMatchesSection — renders on the event detail page (TournamentDetail).
 *
 * Lists the matches the signed-in user is participating in across all
 * rounds of this event. Each row is clickable and navigates to
 * /app/tournament-matches/:matchId/score where the user can enter
 * gross scores hole-by-hole.
 *
 * Component is silent when the user isn't in any matches (e.g. the
 * event owner who doesn't have an event_player participant row, or a
 * player added to the event but not yet assigned to a match). That
 * keeps the event home from showing an empty "My matches" placeholder
 * to people it doesn't apply to.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Play, ArrowUpRight } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';

type Participant = {
  matchId: string;
  eventPlayerId: string;
  teamId: string | null;
  sideNumber: number;
  playingHandicap: number | null;
  displayName: string;
  userId: string | null;
};

type MyMatch = {
  matchId: string;
  matchNumber: number;
  roundId: string;
  roundName: string;
  sessionType: string | null;
  formatType: string;
  status: string;
  teamAId: string | null;
  teamBId: string | null;
  finalResultDisplay: string | null;
  participants: Participant[];
};

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  draft:                 { label: 'Not started',     color: 'var(--text-mut)' },
  published:             { label: 'Ready',           color: 'var(--text-sec)' },
  ready:                 { label: 'Ready',           color: 'var(--text-sec)' },
  in_progress:           { label: 'In progress',     color: 'var(--green-bright)' },
  pending_confirmation:  { label: 'Pending confirm', color: 'var(--gold)' },
  final:                 { label: 'Final',           color: 'var(--text)' },
  disputed:              { label: 'Disputed',        color: 'var(--live-red)' },
  cancelled:             { label: 'Cancelled',       color: 'var(--text-mut)' },
};

function formatHandicap(value: number | null | undefined): string {
  if (value == null) return '—';
  const abs = Math.round(Math.abs(value) * 10) / 10;
  const formatted = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  return value < 0 ? `+${formatted}` : formatted;
}

export function MyMatchesSection({ eventId }: { eventId: string }) {
  const myUserId = useAuthStore((s) => s.userId);
  const [matches, setMatches] = useState<MyMatch[] | null>(null);
  // Distinguish "request failed" from "no matches" — collapsing both to
  // an empty list made a permissions failure look like the player just
  // had no matches, with no way to tell the difference.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    api.getMyMatches(eventId)
      .then((res) => { if (!cancelled) setMatches(res.matches as MyMatch[]); })
      .catch(() => { if (!cancelled) { setMatches([]); setFailed(true); } });
    return () => { cancelled = true; };
  }, [eventId]);

  if (failed) {
    return (
      <section style={{ marginTop: 18, marginBottom: 18 }}>
        <div style={{ fontSize: 13, color: 'var(--text-sec)', fontWeight: 600 }}>
          Couldn't load your matches. Refresh to try again.
        </div>
      </section>
    );
  }

  if (!matches || matches.length === 0) return null;

  return (
    <section style={{ marginTop: 18, marginBottom: 18 }}>
      <header
        style={{
          display: 'flex', alignItems: 'baseline', gap: 10,
          marginBottom: 10, flexWrap: 'wrap',
        }}
      >
        <h2 style={{
          margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: 0.6,
          textTransform: 'uppercase', color: 'var(--green-bright)',
        }}>
          My Matches
        </h2>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-mut)' }}>
          {matches.length}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-mut)', flex: '1 1 auto' }}>
          Tap a match to enter scores hole by hole.
        </span>
        {/* Dedicated round-picker view that Travis asked for alongside
            this list. Both surfaces are live so he can evaluate which
            one feels better in practice. */}
        <Link
          to={`/app/tournaments/${eventId}/my-matches`}
          className="t-btn t-btn-ghost"
          // Bumped from 4×10 padding / 11px font to a 36px tall pill so
          // it clears the touch-target floor on phones. The header row
          // already flex-wraps so growing the link doesn't break layout.
          style={{
            padding: '8px 12px',
            fontSize: 12,
            minHeight: 36,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Round picker <ArrowUpRight width={14} height={14} />
        </Link>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {matches.map((m) => {
          // Find which side I'm on so I can label "you + partner" vs "vs them".
          const me = m.participants.find((p) => p.userId === myUserId);
          const mySide = me?.sideNumber ?? 1;
          const myTeam = m.participants.filter((p) => p.sideNumber === mySide);
          const oppTeam = m.participants.filter((p) => p.sideNumber !== mySide);
          const myNames = myTeam.map((p) => `HCP ${formatHandicap(p.playingHandicap)} ${lastName(p)}`).join(' / ') || 'You';
          const oppNames = oppTeam.map((p) => `HCP ${formatHandicap(p.playingHandicap)} ${lastName(p)}`).join(' / ') || 'Opponent';
          const status = STATUS_BADGE[m.status] ?? { label: m.status, color: 'var(--text-mut)' };
          return (
            <Link
              key={m.matchId}
              to={`/app/tournament-matches/${m.matchId}/score`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(34,197,94,0.12)',
                color: 'var(--green-bright)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Play width={14} height={14} fill="currentColor" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
                    {m.roundName}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-sec)', fontWeight: 700, letterSpacing: 0.3 }}>
                    Match {m.matchNumber}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-sec)', marginTop: 2 }}>
                  <strong style={{ color: 'var(--text)' }}>{myNames}</strong>
                  <span style={{ margin: '0 6px', color: 'var(--text-mut)' }}>vs</span>
                  {oppNames}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                <span style={{
                  fontSize: 11, fontWeight: 900, letterSpacing: 0.8, textTransform: 'uppercase',
                  color: status.color,
                  border: `1px solid ${status.color}`,
                  padding: '2px 6px', borderRadius: 4,
                }}>
                  {status.label}
                </span>
                {m.finalResultDisplay && (
                  <span style={{ fontSize: 11, color: 'var(--text-sec)', fontWeight: 700 }}>
                    {m.finalResultDisplay}
                  </span>
                )}
              </div>
              <ChevronRight width={18} height={18} style={{ color: 'var(--text-mut)', flexShrink: 0 }} />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/** "Limon" from "Pat Limon", "Pat" from just "Pat". Same convention the
 *  scoring tables use so the names match the leaderboard. */
function lastName(p: { displayName: string }): string {
  const name = (p.displayName ?? '').trim();
  if (!name) return 'Player';
  const parts = name.split(/\s+/);
  return parts[parts.length - 1] || name;
}
